import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

/**
 * LoginPage — Adalia. Vidéo "justice" en arrière-plan + carte de connexion en verre.
 * Authentification native email + mot de passe (la plateforme est INDÉPENDANTE de
 * Microsoft 365 — connecteur M365 optionnel ailleurs, plus sur l'écran de connexion).
 */

const BONE = '#FAFAF9';
const FOCUS = 'var(--text-muted)';

// Fond vidéo (hébergé localement : /public/video/login-justice.mp4)
function VideoBackground() {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (v) v.play().catch(() => { /* autoplay bloqué : la teinte reste */ });
  }, []);
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden">
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        loop
        muted
        playsInline
      >
        <source src="/video/login-justice.mp4" type="video/mp4" />
      </video>
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(120deg, rgba(15,14,13,0.94) 0%, rgba(15,14,13,0.58) 45%, rgba(0,0,0,0.88) 100%)' }}
      />
      <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 220px rgba(0,0,0,0.65)' }} />
    </div>
  );
}

export default function LoginPage() {
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login: contextLogin } = useAuth();

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const messageParam = searchParams.get('message');
    const sessionParam = searchParams.get('session');
    if (sessionParam === 'expired') {
      setError('Votre session a expiré. Veuillez vous reconnecter pour continuer.');
    } else if (errorParam) {
      setError(messageParam || 'Erreur de connexion.');
    }
  }, [searchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await contextLogin(email, password, mfaCode || undefined);
      const redirect = searchParams.get('redirect');
      navigate(redirect && redirect.startsWith('/') ? redirect : '/');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.mfa_required) {
        setMfaRequired(true);
        setError(detail.message || 'Code d\'authentification à deux facteurs requis.');
      } else if (!err.response) {
        // Aucune réponse serveur = problème RÉSEAU (souvent une vieille version de
        // l'app en cache), PAS un mauvais mot de passe. Dire la vérité à l'écran.
        setError('Connexion au serveur impossible. Rechargez la page (Ctrl+F5) puis réessayez.');
      } else {
        setError((typeof detail === 'string' ? detail : null) || 'Identifiants incorrects');
      }
    }
    setSubmitting(false);
  };

  const inputBase = 'w-full pl-10 pr-10 py-2.5 rounded-lg text-[13px] text-white placeholder-white/40 bg-white/5 border border-white/10 outline-none transition-colors';
  const onFocus = (e) => { e.currentTarget.style.borderColor = FOCUS; e.currentTarget.style.boxShadow = '0 0 0 3px var(--text-tertiary)'; };
  const onBlur = (e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center px-5 py-10 overflow-hidden">
      <VideoBackground />

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="relative z-20 w-full max-w-[420px]"
      >
        <div
          className="rounded-2xl p-8 backdrop-blur-md"
          style={{
            background: 'rgba(15,14,13,0.58)',
            border: '1px solid var(--border-color)',
            boxShadow: '0 28px 70px rgba(0,0,0,0.5)',
          }}
        >
          {/* Marque — logo officiel (repli auto sur l'emblème vectoriel si absent) */}
          <div className="mb-8">
            <BrandLogo src="/branding/adalia-logo.png?v=2" height={80} fallbackColor={BONE} />
          </div>

          <h1 className="font-serif" style={{ fontSize: 31, fontWeight: 400, lineHeight: 1.08, letterSpacing: '-0.015em', color: BONE }}>
            Bienvenue
          </h1>
          <p className="mt-2 text-white/60" style={{ fontSize: 14 }}>
            Accédez à l'espace sécurisé de votre cabinet.
          </p>

          {error && (
            <div className="mt-5 px-3.5 py-3 rounded-lg" style={{ background: 'rgba(220,80,72,0.14)', border: '1px solid rgba(220,80,72,0.3)', color: '#FCA5A5', fontSize: 13 }}>
              {error}
            </div>
          )}

          {/* Connexion email + mot de passe (méthode principale) */}
          <form onSubmit={onSubmit} className="mt-6 space-y-3.5" data-testid="login-form">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/45" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@votre-cabinet.com"
                required
                autoComplete="email"
                className={inputBase}
                onFocus={onFocus}
                onBlur={onBlur}
                data-testid="login-email"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/45" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe"
                required
                autoComplete="current-password"
                className={inputBase}
                onFocus={onFocus}
                onBlur={onBlur}
                data-testid="login-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white/80 transition-colors"
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {mfaRequired && (
              <div className="relative">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/45" />
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Code à 6 chiffres (Authenticator)"
                  className={inputBase}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  autoFocus
                  data-testid="login-mfa-code"
                />
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg font-medium flex items-center justify-center gap-2 transition-opacity active:scale-[0.99] disabled:opacity-60"
              style={{ background: BONE, color: '#161310', height: 46, fontSize: 14 }}
              data-testid="login-submit"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>

          <p className="text-center mt-7 text-white/45" style={{ fontSize: 11 }}>
            En vous connectant, vous acceptez nos conditions d'utilisation.
          </p>
        </div>

        {/* Signature discrète */}
        <p className="text-center mt-5 font-serif italic" style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>
          « La justice, au quotidien »
        </p>
      </motion.div>
    </div>
  );
}
